import { useLang } from "../context/LanguageContext";
import { useAIAssistant } from "../context/AIAssistantContext";
import { heroBg } from "../assets";

export const Hero = () => {
  const { t } = useLang();
  const { openAssistant } = useAIAssistant();

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const marqueeItems = [...t.hero.marquee, ...t.hero.marquee];

  return (
    <section
      id="home"
      data-testid="hero-section"
      className="relative min-h-[100vh] pt-28 pb-20 overflow-hidden fk-hero-mesh"
    >
      <div className="fk-noise" />

      {/* Background image */}
      <div
        className="absolute inset-0 opacity-[0.45] pointer-events-none"
        style={{
          backgroundImage: `url(${heroBg})`,
          backgroundSize: "cover",
          backgroundPosition: "right center",
          maskImage:
            "radial-gradient(ellipse at right, rgba(0,0,0,0.95) 0%, transparent 65%)",
          WebkitMaskImage:
            "radial-gradient(ellipse at right, rgba(0,0,0,0.95) 0%, transparent 65%)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#09090b] via-[#09090b]/80 to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-8 fk-fade-up">
            <div className="inline-flex items-center gap-2 eyebrow mb-6">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FF5A00] fk-pulse-glow" />
              {t.hero.eyebrow}
            </div>

            <h1
              data-testid="hero-title"
              className="font-[Outfit] font-light text-5xl sm:text-6xl lg:text-7xl xl:text-[92px] leading-[0.98] tracking-tight text-white"
            >
              {t.hero.title1}{" "}
              <span className="font-semibold">{t.hero.title2}</span>{" "}
              <br />
              <span className="fk-gradient-text font-semibold">
                {t.hero.title3}
              </span>
            </h1>

            <p
              data-testid="hero-subtitle"
              className="mt-7 max-w-2xl text-base sm:text-lg text-zinc-400 leading-relaxed"
            >
              {t.hero.subtitle}
            </p>

            <div className="mt-10 flex flex-wrap gap-3">
              <button
                data-testid="hero-cta-primary"
                onClick={openAssistant}
                className="fk-btn fk-btn-primary"
              >
                <i className="fa-solid fa-wand-magic-sparkles" />
                {t.hero.ctaPrimary}
              </button>
              <button
                data-testid="hero-cta-secondary"
                onClick={() => scrollTo("services")}
                className="fk-btn fk-btn-secondary"
              >
                <i className="fa-solid fa-grip" />
                {t.hero.ctaSecondary}
              </button>
            </div>

            <div className="mt-10 flex items-center gap-3 text-xs text-zinc-500">
              <i className="fa-solid fa-shield-halved text-[#FF5A00]" />
              <span data-testid="hero-trust">{t.hero.trust}</span>
            </div>
          </div>

          {/* Side visual — AI preview card */}
          <div className="lg:col-span-4 hidden lg:block">
            <div className="relative">
              <div className="absolute -inset-4 rounded-3xl bg-gradient-to-br from-[#FF5A00]/25 to-transparent blur-2xl" />
              <button
                onClick={openAssistant}
                className="relative fk-glass rounded-3xl p-6 fk-glow w-full text-left hover:border-[#FF5A00]/40 transition-all group"
                data-testid="hero-ai-card"
              >
                <div className="flex items-center gap-2 mb-5">
                  <span className="w-2.5 h-2.5 rounded-full bg-red-500/80" />
                  <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/80" />
                  <span className="w-2.5 h-2.5 rounded-full bg-emerald-500/80" />
                  <span className="ml-auto text-[10px] tracking-widest text-zinc-500">
                    FIKAMI.AI
                  </span>
                </div>

                <div className="space-y-3 text-sm">
                  <div className="flex items-start gap-3">
                    <span className="mt-1 w-7 h-7 rounded-full bg-[#FF5A00]/15 text-[#FF5A00] inline-flex items-center justify-center text-xs">
                      <i className="fa-solid fa-user" />
                    </span>
                    <div className="bg-white/[0.04] border border-white/5 rounded-2xl rounded-tl-sm px-4 py-2.5 text-zinc-200">
                      I need a system to qualify leads automatically.
                    </div>
                  </div>
                  <div className="flex items-start gap-3 flex-row-reverse">
                    <span className="mt-1 w-7 h-7 rounded-full bg-white/10 text-white inline-flex items-center justify-center text-xs">
                      <i className="fa-solid fa-robot" />
                    </span>
                    <div className="bg-[#FF5A00]/10 border border-[#FF5A00]/20 rounded-2xl rounded-tr-sm px-4 py-2.5 text-white">
                      Let&apos;s map it: Website → AI bot → CRM → Appointment.
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="mt-1 w-7 h-7 rounded-full bg-[#FF5A00]/15 text-[#FF5A00] inline-flex items-center justify-center text-xs">
                      <i className="fa-solid fa-bolt" />
                    </span>
                    <div className="bg-white/[0.04] border border-white/5 rounded-2xl rounded-tl-sm px-4 py-2.5 text-zinc-200">
                      <span className="text-[#FF5A00] font-semibold">
                        Ready in 14 days.
                      </span>{" "}
                      Handoff documented.
                    </div>
                  </div>
                </div>

                <div className="mt-6 pt-4 border-t border-white/5 flex items-center justify-between text-[11px] text-zinc-500">
                  <span className="inline-flex items-center gap-1.5">
                    <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
                    LIVE
                  </span>
                  <span className="inline-flex items-center gap-1.5 text-[#FF5A00] group-hover:gap-2 transition-all">
                    Open AI <i className="fa-solid fa-arrow-right text-[10px]" />
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Marquee */}
      <div className="relative mt-20 border-y border-white/5 bg-black/30 overflow-hidden">
        <div
          className="flex gap-10 whitespace-nowrap py-4 fk-marquee"
          data-testid="hero-marquee"
        >
          {marqueeItems.map((item, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-3 text-xs tracking-[0.3em] text-zinc-500"
            >
              <span className="w-1 h-1 rounded-full bg-[#FF5A00]" />
              {item}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
